'use client';

import dayjs from 'dayjs';
import { useMemo, useState, useSyncExternalStore } from 'react';
import { useViewerTimeZone } from '@/hooks/useViewerTimeZone';
import {
    ALL_CONFERENCES,
    buildConferenceOptions,
    isInConference,
    resolveConferenceQuery,
} from '@/packages/sxm/sports/conferences';
import { buildDateOptions, type DateOption } from '@/packages/sxm/sports/schedule';
import { getScheduleConfig } from '@/packages/sxm/sports/scheduleConfig';
import { buildScheduleDays, upcomingItems, type ScheduleItem } from '@/packages/sxm/sports/scheduleItems';

const subscribe = () => () => {};
const getQuery = () => new URLSearchParams(window.location.search).get('schedule');
const getServerQuery = () => null;

function conferencesOf(item: ScheduleItem) {
    if (item.variant === 'event') return [];
    return item.conferences;
}

export function useScheduleFilters({
    items,
    leagueAbbr,
    renderedAt,
}: {
    items: ScheduleItem[];
    leagueAbbr: string;
    renderedAt: number;
}) {
    const timeZone = useViewerTimeZone();
    const query = useSyncExternalStore(subscribe, getQuery, getServerQuery);
    const config = getScheduleConfig(leagueAbbr);
    const now = useMemo(() => dayjs(renderedAt).tz(timeZone), [renderedAt, timeZone]);

    const [selectedDateValue, setSelectedDateValue] = useState<string>();
    const [selectedConference, setSelectedConference] = useState<string>();

    const upcoming = useMemo(() => upcomingItems(items, now, timeZone), [items, now, timeZone]);
    const allConferences = useMemo(() => upcoming.flatMap(conferencesOf), [upcoming]);

    const dateOptions = useMemo(
        () => buildDateOptions(upcoming, config, now, timeZone),
        [upcoming, config, now, timeZone],
    );
    const conferenceOptions = useMemo(() => {
        if (!config.conference) return [];
        return buildConferenceOptions(allConferences);
    }, [allConferences, config.conference]);

    const conference = selectedConference ?? resolveConferenceQuery(allConferences, query) ?? ALL_CONFERENCES;
    const dateOption: DateOption | undefined =
        dateOptions.find((option) => option.value === selectedDateValue) ?? dateOptions[0];

    const days = useMemo(() => {
        const inConference = upcoming.filter((item) => isInConference(conferencesOf(item), conference));
        return buildScheduleDays(inConference, { now, timeZone, range: dateOption?.range });
    }, [upcoming, conference, now, timeZone, dateOption]);

    return {
        timeZone,
        days,
        dateOptions,
        dateValue: dateOption?.value,
        dateLabel: dateOption?.label,
        setDate: setSelectedDateValue,
        conferenceOptions,
        conference,
        setConference: setSelectedConference,
    };
}
